<template>
    <mc-tooltip
        :content="tooltipContent"
        placement="top"
        theme="light"
    >
        <mtd-cascader
            v-model="innerValue"
            :data="options"
            :props="props"
            filterable
            clearable
            :disabled="disabled"
            :multiple="multiple"
            :formatter="formatter"
            v-bind="$attrs"
            @change="handleChange"
        >
            <template
                v-if="showDesc"
                slot-scope="{ node, data }"
            >
                <mc-tooltip
                    :content="data.externalDesc"
                    placement="top"
                >
                    <span>{{ node.label }}</span>
                </mc-tooltip>
            </template>
        </mtd-cascader>
    </mc-tooltip>
</template>

<script>
import arrayToTree from 'utils/arrayToTree';
import { categoryStatusMap } from 'utils/enum';

export default {
    name: 'CategoryCascader',
    props: {
        // 目前只支持单选，后续有多选场景需要改造整个组件
        value: {
            type: [String, Number],
            default: '',
        },
        props: {
            type: Object,
            default: () => ({
                value: 'categoryId',
                label: 'categoryLabel',
            }),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        // 分类类型：1 经营品，2 物资
        categoryType: {
            type: Number,
            default: 0,
        },
        useBgCategory: {
            type: Boolean,
            default: false,
        },
        supplierId: {
            type: Number,
            default: 0,
        },
        // 是否请求过cspu信息，即是否将cspu信息中的后台类目id赋值给组件
        hasQuerySimilarCspuList: {
            type: Boolean,
            default: false,
        },
        // 是否需要展示类目说明
        showDesc: {
            type: Boolean,
            default: false,
        },
        formatter: {
            type: Function,
            default: (labels) => labels[labels.length - 1],
        },
        multiple: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            options: [],
            cacheSelectedIds: [],
            categoryList: [],
            categoryMap: {},
        };
    },
    computed: {
        innerValue: {
            get() {
                if (this.cacheSelectedIds.includes(this.value)) return this.cacheSelectedIds;
                return this.value ? this.getFullCategoryIdsPath(this.value) : [];
            },
            set(val) {
                this.cacheSelectedIds = val;
                this.$emit('input', val && val.length ? val[val.length - 1] : '');
            },
        },
        // 是否需要判断后台类目id是否可提报
        judgeLawful() {
            return [this.hasQuerySimilarCspuList, this.value, this.categoryList];
        },
        tooltipContent() {
            return this.innerValue?.length ? this.categoryMap[this.innerValue[this.innerValue?.length - 1]]?.externalDesc : '';
        },
    },
    watch: {
        judgeLawful: {
            handler(val) {
                const [newFlag, newBgCategoryId, newBgCategoryList] = val;
                this.handleBgCategoryIdOfCspu(newFlag, newBgCategoryId, newBgCategoryList);
            },
            deep: true,
            immediate: true,
        },
    },
    async created() {
        await this.initData();
        this.initValue();
    },
    methods: {
        async initData() {
            let res = [];
            if (this.useBgCategory) {
                if (!this.supplierId) return;
                res = await this.getBgCategoryList();
                // 禁用 待启用，待废弃，已废弃类目
                res = res.map((item) => ({
                    ...item,
                    disabled: [categoryStatusMap.PENDING, categoryStatusMap.AWAITING_DISPOSAL, categoryStatusMap.ABANDONED].includes(item.status),
                }));
            } else {
                res = await this.getCategoryList();
            }

            if (res === 'error') return;

            (res || []).forEach((item) => {
                item.id = item.categoryId || item.id;
                item.name = item.categoryName || item.name;
                item.description = item.categoryDescription || item.description;
                item.categoryLabel = `${item.categoryId}-${item.categoryName || item.name}`;
                this.categoryMap[item.categoryId] = item;
            });

            this.options = arrayToTree(res, 'categoryId', 'parentId');

            this.categoryList = res;
            this.$emit('loaded', res);
        },
        // 管理类目数据
        getCategoryList() {
            const body = {
                // saas 租户：1-小象超市；2-海屯
                tenantId: -1,
            };
            return this.$hPost('/api/vss/shepherd/product/category/query', body).catch(() => 'error');
        },
        // 后台类目数据
        getBgCategoryList() {
            const body = {
                supplierId: this.supplierId,
                categoryType: this.categoryType,
            };
            return this.$hPost('/api/vss/shepherd/product/queryCapableBgCategoryList', body).catch(() => 'error');
        },
        initValue() {
            if (this.value) {
                this.innerValue = this.getFullCategoryIdsPath(this.value);
            }
        },
        handleChange(val) {
            let leafId = '';
            let leafNode = {};
            let ids = [];
            let idsNodes = [];
            if (!val || !val.length) {
                this.$emit('change', leafId, leafNode, ids, idsNodes);
                return;
            }
            leafId = val[val.length - 1];
            leafNode = this.categoryMap[leafId];
            ids = val;
            idsNodes = val.map((id) => this.categoryMap[id]);
            this.$emit('change', leafId, leafNode, ids, idsNodes);
        },
        getFullCategoryIdsPath(leafId) {
            const categorys = [leafId];
            let target = this.categoryMap[leafId];
            if (!target) return [];
            while (target && target.parentId) {
                categorys.unshift(target.parentId);
                target = this.categoryMap[target.parentId];
            }
            return categorys;
        },
        handleBgCategoryIdOfCspu(newFlag, newBgCategoryId, newBgCategoryList) {
            if (!newFlag || !newBgCategoryId || !newBgCategoryList.length) return;
            // cspu赋值给选择器的类目id，不在类目数据中 -> 清空类目id
            if (!newBgCategoryList.map((item) => item.id).includes(newBgCategoryId)) {
                this.innerValue = []; // 清空级联选择器的绑定值
                this.$emit('input', ''); // 清空表单中的类目id
            }
        },
    },
};
</script>

<style>
    .el-cascader-menu__item--extensible:after {
        right: 8px;
    }
</style>
